import React, { useState, useMemo, useEffect } from 'react';
import { useChemistryStore } from '../../stores/chemistryStore';
import { Molecule } from '../../types/chemistry';
import { StructureEditorModal } from './StructureEditor';

interface SearchPanelProps {
  fileId: string;
  columns: string[];
  molecules: Molecule[];
  onFilteredResults: (filtered: Molecule[] | null, matchIds: Set<number> | null) => void;
  showMatchesOnly: boolean;
  onShowMatchesOnlyChange: (checked: boolean) => void;
}

type SearchTab = 'structure' | 'attributes';
type SearchType = 'substructure' | 'similarity' | 'exact';
type StereoMode = 'strict' | 'ignore';
type TautomerMode = 'exact' | 'inchi' | 'rsmarts';
type Operator = 'equals' | 'not_equals' | 'greater_than' | 'less_than' | 'greater_or_equal' | 'less_or_equal' | 'contains' | 'starts_with' | 'ends_with' | 'is_empty' | 'is_not_empty';

interface AttributeCondition {
  id: string;
  property: string;
  operator: Operator;
  value: string;
}

const OPERATORS: { value: Operator; label: string }[] = [
  { value: 'equals', label: 'equals' },
  { value: 'not_equals', label: 'not equals' },
  { value: 'greater_than', label: 'greater than' },
  { value: 'less_than', label: 'less than' },
  { value: 'greater_or_equal', label: 'greater or equal' },
  { value: 'less_or_equal', label: 'less or equal' },
  { value: 'contains', label: 'contains' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'ends_with', label: 'ends with' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
];

const SearchPanel: React.FC<SearchPanelProps> = ({
  fileId,
  columns,
  molecules,
  onFilteredResults,
  showMatchesOnly,
  onShowMatchesOnlyChange,
}) => {
  const { searchStructure, clearSearch, isSearching, searchResults } = useChemistryStore();

  // Tab state
  const [activeTab, setActiveTab] = useState<SearchTab>('structure');

  // Structure search state
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [drawnStructure, setDrawnStructure] = useState<{ molfile: string; smiles: string } | null>(null);
  const [structureImage, setStructureImage] = useState<string | null>(null);
  const [searchType, setSearchType] = useState<SearchType>('substructure');
  const [threshold, setThreshold] = useState(80);
  const [structureError, setStructureError] = useState<string | null>(null);

  // Chemistry search options
  const [stereoMode, setStereoMode] = useState<StereoMode>('strict');
  const [tautomerMode, setTautomerMode] = useState<TautomerMode>('exact');
  const [includeResonance, setIncludeResonance] = useState(false);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  // Attribute search state
  const [conditions, setConditions] = useState<AttributeCondition[]>([
    { id: '1', property: '', operator: 'equals', value: '' },
  ]);
  const [logicOperator, setLogicOperator] = useState<'AND' | 'OR'>('AND');
  const [attributeResults, setAttributeResults] = useState<Molecule[] | null>(null);
  const [attributeMatchIds, setAttributeMatchIds] = useState<Set<number> | null>(null);
  const [attributeError, setAttributeError] = useState<string | null>(null);

  // Generate unique ID for conditions
  const generateId = () => Math.random().toString(36).substr(2, 9);

  // Reset local state when file changes
  useEffect(() => {
    setDrawnStructure(null);
    setStructureImage(null);
    setStructureError(null);
    setConditions([{ id: '1', property: '', operator: 'equals', value: '' }]);
    setAttributeResults(null);
    setAttributeMatchIds(null);
    setAttributeError(null);
  }, [fileId]);

  // Clear error when search results are cleared externally (e.g. "Clear search" button in results bar)
  useEffect(() => {
    if (!searchResults && !isSearching) {
      setStructureError(null);
    }
  }, [searchResults, isSearching]);

  // Fetch structure image when structure is drawn
  useEffect(() => {
    const fetchImage = async () => {
      if (!drawnStructure?.smiles && !drawnStructure?.molfile) return;
      try {
        const response = await fetch('/api/v2/indigo/render', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            structure: {
              value: drawnStructure.smiles || drawnStructure.molfile,
              type: drawnStructure.smiles ? 'RAW' : 'MOL'
            },
            width: '200px',
            height: '120px'
          })
        });
        if (response.ok) {
          const svg = await response.text();
          setStructureImage(svg);
        }
      } catch (e) {
        console.error('Failed to render structure:', e);
      }
    };
    fetchImage();
  }, [drawnStructure]);

  // Structure Search Handlers
  const handleStructureSave = (molfile: string, smiles: string) => {
    console.log('[SearchPanel] handleStructureSave called');
    console.log('[SearchPanel] molfile length:', molfile?.length, 'smiles:', smiles);
    console.log('[SearchPanel] molfile first 200 chars:', molfile?.substring(0, 200));
    setDrawnStructure({ molfile, smiles });
    setIsEditorOpen(false);
  };

  const handleStructureSearch = async () => {
    console.log('[SearchPanel] handleStructureSearch called');
    console.log('[SearchPanel] drawnStructure:', drawnStructure);
    console.log('[SearchPanel] fileId:', fileId);
    console.log('[SearchPanel] searchType:', searchType);

    if (!drawnStructure?.molfile && !drawnStructure?.smiles) {
      setStructureError('Please draw a structure first');
      return;
    }

    setStructureError(null);

    const queryStructure = drawnStructure.molfile || drawnStructure.smiles;
    console.log('[SearchPanel] queryStructure (first 200 chars):', queryStructure.substring(0, 200));

    try {
      await searchStructure(fileId, queryStructure, searchType, {
        threshold: searchType === 'similarity' ? threshold / 100 : undefined,
        stereoMode,
        tautomerMode,
        resonance: includeResonance,
      });
      console.log('[SearchPanel] searchStructure completed successfully');
    } catch (err: any) {
      console.error('[SearchPanel] searchStructure error:', err);
      const msg = err.response?.data?.detail || err.message || 'Search failed';
      setStructureError(msg === 'Search failed' ? 'Search failed — check that the structure is valid' : msg);
    }
  };

  const handleStructureReset = () => {
    setDrawnStructure(null);
    setStructureImage(null);
    setStructureError(null);
    clearSearch();
    onFilteredResults(null, null);
  };

  // Attribute Search Handlers
  const addCondition = () => {
    setConditions([
      ...conditions,
      { id: generateId(), property: columns[0] || '', operator: 'equals', value: '' },
    ]);
  };

  const removeCondition = (id: string) => {
    if (conditions.length > 1) {
      setConditions(conditions.filter((c) => c.id !== id));
    }
  };

  const updateCondition = (id: string, field: keyof AttributeCondition, value: string) => {
    setConditions(
      conditions.map((c) => (c.id === id ? { ...c, [field]: value } : c))
    );
  };

  const evaluateCondition = (mol: Molecule, condition: AttributeCondition): boolean => {
    const propValue = mol.properties?.[condition.property];

    // Helper to check if a value is considered "empty"
    // Empty means: undefined, null, empty string, or whitespace-only string
    // Note: 0 is NOT empty (zero is a valid value)
    const isEmpty = (val: any): boolean => {
      if (val === undefined || val === null) return true;
      if (typeof val === 'string' && val.trim() === '') return true;
      return false;
    };

    // Handle is_empty and is_not_empty first (don't need value check)
    if (condition.operator === 'is_empty') {
      return isEmpty(propValue);
    }
    if (condition.operator === 'is_not_empty') {
      return !isEmpty(propValue);
    }

    // For other operators, null/undefined means no match
    if (propValue === undefined || propValue === null) return false;

    const strValue = String(propValue).toLowerCase();
    const condValue = condition.value.toLowerCase();
    const numPropValue = parseFloat(String(propValue));
    const numCondValue = parseFloat(condition.value);

    switch (condition.operator) {
      case 'equals':
        return strValue === condValue;
      case 'not_equals':
        return strValue !== condValue;
      case 'greater_than':
        return !isNaN(numPropValue) && !isNaN(numCondValue) && numPropValue > numCondValue;
      case 'less_than':
        return !isNaN(numPropValue) && !isNaN(numCondValue) && numPropValue < numCondValue;
      case 'greater_or_equal':
        return !isNaN(numPropValue) && !isNaN(numCondValue) && numPropValue >= numCondValue;
      case 'less_or_equal':
        return !isNaN(numPropValue) && !isNaN(numCondValue) && numPropValue <= numCondValue;
      case 'contains':
        return strValue.includes(condValue);
      case 'starts_with':
        return strValue.startsWith(condValue);
      case 'ends_with':
        return strValue.endsWith(condValue);
      default:
        return false;
    }
  };

  const handleAttributeSearch = () => {
    // Filter out empty conditions (is_empty/is_not_empty don't need a value)
    const validConditions = conditions.filter((c) =>
      c.property && (c.value || c.operator === 'is_empty' || c.operator === 'is_not_empty')
    );
    if (validConditions.length === 0) {
      setAttributeResults(null);
      setAttributeMatchIds(null);
      onFilteredResults(null, null);
      return;
    }

    // Use searchResults.molecules when structure search is active (for large files)
    // Fall back to molecules for small files or when no structure search
    const sourceData = searchResults?.molecules && searchResults.molecules.length > 0
      ? searchResults.molecules
      : molecules;

    if (sourceData.length === 0) {
      console.warn('[SearchPanel] No molecules available for attribute filtering');
      // For large files (SSRM mode), molecules aren't loaded client-side
      // Guide user to do a structure search first to load matching molecules
      setAttributeError(
        'For large files, please do a structure search first to load molecules, then filter by attributes.'
      );
      setAttributeResults(null);
      setAttributeMatchIds(null);
      return;
    }

    // Clear any previous error
    setAttributeError(null);

    const filtered = sourceData.filter((mol) => {
      const results = validConditions.map((c) => evaluateCondition(mol, c));
      return logicOperator === 'AND' ? results.every(Boolean) : results.some(Boolean);
    });

    const matchIds = new Set(filtered.map((m) => m.id));
    setAttributeResults(filtered);
    setAttributeMatchIds(matchIds);

    if (showMatchesOnly) {
      onFilteredResults(filtered, matchIds);
    } else {
      onFilteredResults(null, matchIds);
    }
  };

  const handleAttributeReset = () => {
    setConditions([{ id: '1', property: '', operator: 'equals', value: '' }]);
    setAttributeResults(null);
    setAttributeMatchIds(null);
    setAttributeError(null);
    onFilteredResults(null, null);
  };

  // Handle show matches only toggle - notify parent component
  const handleShowMatchesOnlyToggle = (checked: boolean) => {
    onShowMatchesOnlyChange(checked);

    // For attribute search, also update the filtered results
    if (activeTab === 'attributes' && attributeResults) {
      if (checked) {
        onFilteredResults(attributeResults, attributeMatchIds);
      } else {
        onFilteredResults(null, attributeMatchIds);
      }
    }
  };

  // Get current result count - prioritize structure search results when active
  const resultCount = useMemo(() => {
    // Always show structure search count when there's an active structure search
    if (searchResults?.total != null && searchResults.total > 0) {
      return searchResults.total;
    }
    // Fall back to attribute results if on attributes tab
    if (activeTab === 'attributes') {
      return attributeResults?.length || 0;
    }
    return searchResults?.total || 0;
  }, [activeTab, searchResults, attributeResults]);

  const hasResults = activeTab === 'structure' ? !!searchResults : !!attributeResults;

  return (
    <div className="flex flex-col h-full">
      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('structure')}
          className={`flex-1 px-4 py-3 text-sm font-medium ${
            activeTab === 'structure'
              ? 'text-emerald-600 border-b-2 border-emerald-500 bg-white'
              : 'text-gray-500 hover:text-gray-700 bg-gray-50'
          }`}
        >
          By Structure
        </button>
        <button
          onClick={() => setActiveTab('attributes')}
          className={`flex-1 px-4 py-3 text-sm font-medium ${
            activeTab === 'attributes'
              ? 'text-emerald-600 border-b-2 border-emerald-500 bg-white'
              : 'text-gray-500 hover:text-gray-700 bg-gray-50'
          }`}
        >
          By Attributes
        </button>
      </div>

      {/* Results Bar */}
      {hasResults && (
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-200">
          <div className="flex items-center justify-between">
            <span className="text-sm text-amber-700 font-medium">
              Found {resultCount} matching molecules
            </span>
          </div>
          {/* Show active chemistry options */}
          {activeTab === 'structure' && (stereoMode !== 'strict' || tautomerMode !== 'exact' || includeResonance) && (
            <div className="flex flex-wrap gap-1 mt-1">
              {stereoMode === 'ignore' && (
                <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                  Stereo: ignore
                </span>
              )}
              {tautomerMode !== 'exact' && (
                <span className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">
                  Tautomers: {tautomerMode}
                </span>
              )}
              {includeResonance && (
                <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                  +Resonance
                </span>
              )}
            </div>
          )}
          <label className="flex items-center mt-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showMatchesOnly}
              onChange={(e) => handleShowMatchesOnlyToggle(e.target.checked)}
              className="rounded border-gray-300 text-emerald-500 focus:ring-emerald-500"
            />
            <span className="ml-2 text-xs text-gray-600">Show matches only</span>
          </label>
        </div>
      )}

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'structure' ? (
          /* Structure Search Tab */
          <div className="space-y-4">
            {/* Draw Structure Button or Preview */}
            {!drawnStructure ? (
              <button
                onClick={() => setIsEditorOpen(true)}
                className="w-full px-4 py-8 text-sm text-gray-600 bg-white border-2 border-gray-300 border-dashed rounded-lg hover:bg-gray-50 hover:border-emerald-400 transition-colors"
              >
                <svg className="w-8 h-8 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
                </svg>
                <span className="font-medium">Draw Structure</span>
                <p className="text-xs text-gray-400 mt-1">Click to open Ketcher editor</p>
              </button>
            ) : (
              <div className="p-3 bg-white border border-gray-300 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-500">Query Structure</span>
                  <button
                    onClick={handleStructureReset}
                    className="text-gray-400 hover:text-red-500 text-xs"
                  >
                    Clear
                  </button>
                </div>
                {structureImage ? (
                  <div
                    className="w-full h-32 flex items-center justify-center bg-white border rounded overflow-hidden"
                    dangerouslySetInnerHTML={{ __html: structureImage }}
                  />
                ) : (
                  <div className="w-full h-32 flex items-center justify-center bg-gray-50 border rounded">
                    <span className="text-gray-400 text-sm">Loading preview...</span>
                  </div>
                )}
                <button
                  onClick={() => setIsEditorOpen(true)}
                  className="mt-2 text-xs text-emerald-600 hover:text-emerald-700"
                >
                  Edit structure
                </button>
              </div>
            )}

            {/* Search Options - Only show when structure is drawn */}
            {drawnStructure && (
              <>
                {/* Search Type */}
                <div>
                  <label className="block text-xs text-gray-500 mb-2">Search Type</label>
                  <select
                    value={searchType}
                    onChange={(e) => setSearchType(e.target.value as SearchType)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="substructure">Substructure</option>
                    <option value="similarity">Similarity</option>
                    <option value="exact">Exact Match</option>
                  </select>
                </div>

                {/* Similarity Threshold */}
                {searchType === 'similarity' && (
                  <div>
                    <label className="block text-xs text-gray-500 mb-2">
                      Similarity Threshold: {threshold}%
                    </label>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={threshold}
                      onChange={(e) => setThreshold(Number(e.target.value))}
                      className="w-full accent-emerald-500"
                    />
                    <div className="flex justify-between text-xs text-gray-400 mt-1">
                      <span>0%</span>
                      <span>100%</span>
                    </div>
                  </div>
                )}

                {/* Advanced Chemistry Options Toggle */}
                <button
                  type="button"
                  onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                  className="flex items-center text-xs text-gray-500 hover:text-emerald-600"
                >
                  <svg
                    className={`w-3 h-3 mr-1 transition-transform ${showAdvancedOptions ? 'rotate-90' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  Chemistry Options
                </button>

                {/* Advanced Chemistry Options */}
                {showAdvancedOptions && (
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
                    {/* Stereo Mode */}
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Stereochemistry</label>
                      <select
                        value={stereoMode}
                        onChange={(e) => setStereoMode(e.target.value as StereoMode)}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      >
                        <option value="strict">Strict (R/S, E/Z must match)</option>
                        <option value="ignore">Ignore (find all stereoisomers)</option>
                      </select>
                      <p className="text-xs text-gray-400 mt-1">
                        {stereoMode === 'strict'
                          ? 'L-alanine will NOT match D-alanine'
                          : 'L-alanine WILL match D-alanine'}
                      </p>
                    </div>

                    {/* Tautomer Mode */}
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Tautomers</label>
                      <select
                        value={tautomerMode}
                        onChange={(e) => setTautomerMode(e.target.value as TautomerMode)}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      >
                        <option value="exact">Exact (tautomers are different)</option>
                        <option value="inchi">InChI (match tautomers)</option>
                        <option value="rsmarts">RSMARTS (match tautomers)</option>
                      </select>
                      <p className="text-xs text-gray-400 mt-1">
                        {tautomerMode === 'exact'
                          ? 'Keto form ≠ enol form'
                          : 'Keto form = enol form'}
                      </p>
                    </div>

                    {/* Resonance - only for substructure */}
                    {searchType === 'substructure' && (
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={includeResonance}
                          onChange={(e) => setIncludeResonance(e.target.checked)}
                          className="rounded border-gray-300 text-emerald-500 focus:ring-emerald-500"
                        />
                        <span className="ml-2 text-xs text-gray-600">Include resonance structures</span>
                      </label>
                    )}
                  </div>
                )}

                {/* Error */}
                {structureError && (
                  <div className="p-2 bg-red-50 border border-red-200 rounded text-red-600 text-xs flex items-start justify-between gap-2">
                    <span>{structureError}</span>
                    <button
                      onClick={() => setStructureError(null)}
                      className="text-red-400 hover:text-red-600 flex-shrink-0"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}

                {/* Search Button */}
                <button
                  onClick={handleStructureSearch}
                  disabled={isSearching}
                  className="w-full px-4 py-2 bg-emerald-500 text-white text-sm font-medium rounded-md hover:bg-emerald-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {isSearching ? 'Searching...' : 'Search'}
                </button>
              </>
            )}

            {/* Help when no structure */}
            {!drawnStructure && (
              <div className="p-3 bg-gray-100 rounded-md">
                <div className="text-xs text-gray-600 space-y-1">
                  <p className="font-medium">Structure Search</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Draw a query structure using Ketcher</li>
                    <li>Search by substructure, similarity, or exact match</li>
                    <li>Results show molecules containing your query</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Attributes Search Tab */
          <div className="space-y-4">
            {/* Conditions */}
            <div className="space-y-3">
              {conditions.map((condition, index) => (
                <div key={condition.id}>
                  {/* Logic operator between conditions */}
                  {index > 0 && (
                    <div className="flex items-center justify-center my-2">
                      <button
                        onClick={() => setLogicOperator(logicOperator === 'AND' ? 'OR' : 'AND')}
                        className="px-3 py-1 text-xs font-medium text-emerald-700 bg-emerald-100 rounded-full hover:bg-emerald-200"
                      >
                        {logicOperator}
                      </button>
                    </div>
                  )}

                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    {/* Property dropdown */}
                    <select
                      value={condition.property}
                      onChange={(e) => updateCondition(condition.id, 'property', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md mb-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      <option value="">Select property...</option>
                      {columns.map((col) => (
                        <option key={col} value={col}>
                          {col}
                        </option>
                      ))}
                    </select>

                    {/* Operator and value row - fixed layout */}
                    <div className="flex items-center gap-2">
                      <select
                        value={condition.operator}
                        onChange={(e) => updateCondition(condition.id, 'operator', e.target.value)}
                        className="w-32 flex-shrink-0 px-2 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      >
                        {OPERATORS.map((op) => (
                          <option key={op.value} value={op.value}>
                            {op.label}
                          </option>
                        ))}
                      </select>
                      {condition.operator !== 'is_empty' && condition.operator !== 'is_not_empty' && (
                        <input
                          type="text"
                          value={condition.value}
                          onChange={(e) => updateCondition(condition.id, 'value', e.target.value)}
                          placeholder="Value..."
                          className="flex-1 min-w-0 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                      )}
                      <button
                        onClick={() => removeCondition(condition.id)}
                        className={`flex-shrink-0 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 ${
                          conditions.length === 1 ? 'invisible' : ''
                        }`}
                        disabled={conditions.length === 1}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Add Condition Button */}
            <button
              onClick={addCondition}
              className="w-full px-4 py-2 text-sm text-emerald-600 bg-emerald-50 border border-emerald-200 border-dashed rounded-md hover:bg-emerald-100"
            >
              + Add Condition
            </button>

            {/* Buttons */}
            <div className="flex space-x-2">
              <button
                onClick={handleAttributeSearch}
                disabled={!conditions.some((c) => c.property && (c.value || c.operator === 'is_empty' || c.operator === 'is_not_empty'))}
                className="flex-1 px-4 py-2 bg-emerald-500 text-white text-sm font-medium rounded-md hover:bg-emerald-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Search
              </button>
              <button
                onClick={handleAttributeReset}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Clear
              </button>
            </div>

            {/* Error message for large files */}
            {attributeError && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
                <p className="text-sm text-amber-700">{attributeError}</p>
                <p className="text-xs text-amber-600 mt-1">
                  Tip: Use the "By Structure" tab first to find matching molecules.
                </p>
              </div>
            )}

            {/* Help */}
            <div className="p-3 bg-gray-100 rounded-md">
              <div className="text-xs text-gray-600 space-y-1">
                <p className="font-medium">Search Tips:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Use &gt;, &lt; for numeric properties (MW, LogP)</li>
                  <li>Use "contains" for text properties</li>
                  <li>Click AND/OR to toggle logic</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Ketcher Modal */}
      <StructureEditorModal
        isOpen={isEditorOpen}
        onClose={() => setIsEditorOpen(false)}
        onSave={handleStructureSave}
        initialStructure={drawnStructure?.molfile || ''}
        title="Draw Search Structure"
      />
    </div>
  );
};

export default SearchPanel;
